import { useState, useEffect, useRef } from "react";
import {
  ChevronLeft, X, CreditCard, Barcode as BarcodeIcon,
  Check, Loader2, Camera, AlertCircle, RefreshCw, Upload,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API          = `${BACKEND_URL}/api`;
const PRIMARY      = "#2D5BFF";
const PRIMARY_DARK = "#1B3DBF";
const GRADIENT     = `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`;

const StepIndicator = ({ step }) => (
  <div className="flex items-center gap-2 mb-2">
    <div style={{ width: 32, height: 4, borderRadius: 999, backgroundColor: PRIMARY }} />
    <div style={{ width: 32, height: 4, borderRadius: 999, backgroundColor: step === 2 ? PRIMARY : "#DBEAFE" }} />
    <span style={{ color: "#6B7280", fontSize: 13, fontWeight: 600, fontFamily: "DM Sans, sans-serif", marginLeft: 6 }}>
      Step {step} of 2
    </span>
  </div>
);

export default function AddCardSheet({ isOpen, onClose, onSaved }) {
  const [step,          setStep]          = useState(1);
  const [cardName,      setCardName]      = useState("");
  const [barcode,       setBarcode]       = useState("");
  const [notes,         setNotes]         = useState("");
  const [errors,        setErrors]        = useState({});
  const [saving,        setSaving]        = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [errorMsg,      setErrorMsg]      = useState("");
  const [cameraReady,   setCameraReady]   = useState(false);
  const [cameraBlocked, setCameraBlocked] = useState(false);

  const videoRef    = useRef(null);
  const streamRef   = useRef(null);
  const overlayRef  = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen || step !== 1) return;
    let cancelled = false;
    setCameraReady(false);
    setCameraBlocked(false);
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 } } })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch(() => { if (!cancelled) setCameraBlocked(true); });
    return () => {
      cancelled = true;
      if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    };
  }, [isOpen, step]);

  useEffect(() => {
    if (!isOpen) {
      if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
      return;
    }
    setStep(1); setCardName(""); setBarcode(""); setNotes("");
    setErrors({}); setSaving(false); setCapturedImage(null);
    setErrorMsg(""); setCameraReady(false); setCameraBlocked(false);
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = "unset"; document.removeEventListener("keydown", onKey); };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const stopStream = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  };

  // Shared scan logic — takes base64 JPEG and calls /api/scan-card
  const scanBase64 = async (base64) => {
    stopStream();
    setCapturedImage(base64);
    setStep("scanning");
    try {
      const res = await axios.post(
        `${API}/scan-card`,
        { image: base64, media_type: "image/jpeg" },
        { withCredentials: true }
      );
      const d = res.data;
      if (d.success && (d.data?.card_name || d.data?.card_number)) {
        if (d.data.card_name)   setCardName(d.data.card_name);
        if (d.data.card_number) setBarcode(d.data.card_number);
        setStep(2);
      } else {
        setErrorMsg(d.error || "We couldn't read the card name or number. Try again with better lighting.");
        setStep("error");
      }
    } catch (err) {
      if (err.response?.status === 401) { toast.error("Session expired"); onClose(); return; }
      setErrorMsg("Something went wrong. Check your connection and try again.");
      setStep("error");
    }
  };

  // Upload a photo from gallery
  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so same file can be re-selected if needed
    e.target.value = "";
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(",")[1];
      scanBase64(base64);
    };
    reader.readAsDataURL(file);
  };

  const handleCapture = async () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    const base64 = canvas.toDataURL("image/jpeg", 0.85).split(",")[1];
    await scanBase64(base64);
  };

  const handleSave = async () => {
    const errs = {};
    if (!cardName.trim()) errs.card_name      = "Enter card name";
    if (!barcode.trim())  errs.barcode_number = "Enter card number";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/cards`,
        { card_name: cardName.trim(), barcode_number: barcode.replace(/\s+/g, ""), notes: notes.trim() || null },
        { withCredentials: true }
      );
      if (res.data.success) { toast.success("Card saved"); onSaved?.(); onClose(); }
    } catch (err) {
      if (err.response?.data?.detail?.validation_errors) setErrors(err.response.data.detail.validation_errors);
      else toast.error("Couldn't save card");
    } finally { setSaving(false); }
  };

  const goManual = () => { setCardName(""); setBarcode(""); setStep(2); };

  const renderStep1 = () => (
    <>
      <div className="px-5 flex-shrink-0">
        <StepIndicator step={1} />
        <h2 style={{ color: "#0F172A", fontSize: 24, fontWeight: 700, fontFamily: "Manrope, sans-serif", marginTop: 6 }}>
          Scan your card
        </h2>
        <p style={{ color: "#64748B", fontSize: 14, fontFamily: "DM Sans, sans-serif", marginTop: 4 }}>
          Take a photo of your loyalty card
        </p>
      </div>
      <div className="flex-1 flex flex-col mt-4">
        <div className="relative overflow-hidden mx-5" style={{ borderRadius: 16, backgroundColor: "#000", height: 265 }}>
          {cameraBlocked ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6">
              <Camera className="w-10 h-10" style={{ color: "#475569" }} />
              <p style={{ color: "#94A3B8", fontSize: 13, fontFamily: "DM Sans, sans-serif", textAlign: "center" }}>
                Camera access blocked. Allow camera in browser settings, or enter details manually.
              </p>
            </div>
          ) : (
            <>
              <video ref={videoRef} autoPlay playsInline muted
                onCanPlay={() => setCameraReady(true)}
                style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              {[
                { top: 16, left: 16,   borderTop: `3px solid ${PRIMARY}`, borderLeft:  `3px solid ${PRIMARY}` },
                { top: 16, right: 16,  borderTop: `3px solid ${PRIMARY}`, borderRight: `3px solid ${PRIMARY}` },
                { bottom: 16, left: 16,  borderBottom: `3px solid ${PRIMARY}`, borderLeft:  `3px solid ${PRIMARY}` },
                { bottom: 16, right: 16, borderBottom: `3px solid ${PRIMARY}`, borderRight: `3px solid ${PRIMARY}` },
              ].map((s, i) => <div key={i} className="absolute" style={{ width: 26, height: 26, ...s }} />)}
              {cameraReady && (
                <div className="absolute left-10 right-10"
                  style={{ height: 2, background: `linear-gradient(90deg, transparent, ${PRIMARY}, transparent)`, animation: "scanLine 2s ease-in-out infinite" }} />
              )}
              {!cameraReady && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin" style={{ color: "rgba(255,255,255,0.5)" }} />
                </div>
              )}
            </>
          )}
        </div>
        <div className="px-5 pt-3 pb-5 space-y-3">
          {/* Hidden file input for gallery upload */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleUpload}
          />

          <p style={{ color: "#94A3B8", fontSize: 12, fontFamily: "DM Sans, sans-serif", textAlign: "center" }}>
            Card flat · good lighting · number fully visible
          </p>

          {/* Take photo + Upload side by side */}
          {!cameraBlocked && (
            <div className="flex gap-2">
              <button onClick={handleCapture} disabled={!cameraReady}
                className="flex-1 flex items-center justify-center gap-2 rounded-2xl"
                style={{ background: cameraReady ? GRADIENT : "#E2E8F0", color: cameraReady ? "#FFFFFF" : "#94A3B8", height: 52, fontSize: 14, fontWeight: 700, fontFamily: "DM Sans, sans-serif", boxShadow: cameraReady ? "0 8px 20px rgba(45,91,255,0.22)" : "none" }}
                data-testid="capture-btn">
                <Camera className="w-4 h-4" />
                Take photo
              </button>
              <button onClick={() => fileInputRef.current?.click()}
                className="flex items-center justify-center gap-2 rounded-2xl"
                style={{ height: 52, paddingLeft: 16, paddingRight: 16, backgroundColor: "#F1F5F9", color: "#475569", fontSize: 14, fontWeight: 700, fontFamily: "DM Sans, sans-serif", flexShrink: 0 }}
                data-testid="upload-btn">
                <Upload className="w-4 h-4" />
                Upload
              </button>
            </div>
          )}

          {/* Upload-only when camera is blocked */}
          {cameraBlocked && (
            <button onClick={() => fileInputRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 rounded-2xl"
              style={{ background: GRADIENT, color: "#FFFFFF", height: 52, fontSize: 15, fontWeight: 700, fontFamily: "DM Sans, sans-serif", boxShadow: "0 8px 20px rgba(45,91,255,0.22)" }}
              data-testid="upload-btn-fallback">
              <Upload className="w-5 h-5" />
              Upload photo of card
            </button>
          )}
          <button onClick={goManual} className="w-full rounded-xl"
            style={{ border: `1.5px solid ${PRIMARY}`, color: PRIMARY, backgroundColor: "#FFFFFF", padding: "13px 16px", fontSize: 15, fontWeight: 700, fontFamily: "DM Sans, sans-serif" }}
            data-testid="enter-manually-btn">
            Enter card details manually
          </button>
        </div>
      </div>
    </>
  );

  const renderScanning = () => (
    <div className="flex-1 flex flex-col items-center justify-center px-6 gap-5">
      {capturedImage && (
        <div className="relative w-full" style={{ maxWidth: 320, borderRadius: 16, overflow: "hidden", height: 200 }}>
          <img src={`data:image/jpeg;base64,${capturedImage}`} alt="Captured"
            style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.5 }} />
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3" style={{ background: "rgba(15,23,42,0.45)" }}>
            <Loader2 className="w-9 h-9 animate-spin" style={{ color: "#FFFFFF" }} />
            <p style={{ color: "#FFFFFF", fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>Reading card details...</p>
          </div>
          <div className="absolute left-0 right-0"
            style={{ height: 2, background: `linear-gradient(90deg, transparent, ${PRIMARY}, transparent)`, animation: "scanLine 1.5s ease-in-out infinite" }} />
        </div>
      )}
      <p style={{ color: "#64748B", fontSize: 13, fontFamily: "DM Sans, sans-serif", textAlign: "center" }}>
        AI is identifying your card — just a moment
      </p>
    </div>
  );

  const renderError = () => (
    <div className="flex-1 flex flex-col px-5">
      <div className="flex-1 flex flex-col items-center justify-center gap-4">
        {capturedImage && (
          <div style={{ width: 96, height: 64, borderRadius: 10, overflow: "hidden", opacity: 0.6 }}>
            <img src={`data:image/jpeg;base64,${capturedImage}`} alt="Captured" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </div>
        )}
        <div className="flex items-center justify-center" style={{ width: 60, height: 60, borderRadius: 999, backgroundColor: "#FEF2F2" }}>
          <AlertCircle className="w-7 h-7" style={{ color: "#EF4444" }} />
        </div>
        <div className="text-center">
          <p style={{ color: "#0F172A", fontSize: 17, fontWeight: 700, fontFamily: "Manrope, sans-serif", marginBottom: 8 }}>Couldn't read the card</p>
          <p style={{ color: "#64748B", fontSize: 14, fontFamily: "DM Sans, sans-serif", lineHeight: 1.55, maxWidth: 290 }}>{errorMsg}</p>
        </div>
        <div style={{ backgroundColor: "#F8FAFC", borderRadius: 12, padding: "12px 16px", width: "100%", border: "1px solid #E2E8F0" }}>
          <p style={{ color: "#475569", fontSize: 12, fontFamily: "DM Sans, sans-serif", lineHeight: 1.7, margin: 0 }}>
            <strong>Tips for a better scan:</strong><br />
            · Hold the card flat — no bend or curl<br />
            · Card number must be fully in frame<br />
            · Good lighting, avoid glare and shadows<br />
            · Fill the frame and hold steady
          </p>
        </div>
      </div>
      <div className="pb-6 space-y-3">
        <button onClick={() => { setCapturedImage(null); setStep(1); }}
          className="w-full flex items-center justify-center gap-2 rounded-2xl"
          style={{ background: GRADIENT, color: "#FFFFFF", height: 52, fontSize: 15, fontWeight: 700, fontFamily: "DM Sans, sans-serif", boxShadow: "0 8px 20px rgba(45,91,255,0.22)" }}
          data-testid="retry-scan-btn">
          <RefreshCw className="w-5 h-5" />
          Try again
        </button>
        <button onClick={() => fileInputRef.current?.click()}
          className="w-full flex items-center justify-center gap-2 rounded-xl"
          style={{ border: "1.5px solid #E2E8F0", color: "#475569", backgroundColor: "#FFFFFF", height: 48, fontSize: 15, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}
          data-testid="upload-from-error-btn">
          <Upload className="w-4 h-4" />
          Upload a photo instead
        </button>
        <button onClick={goManual} className="w-full text-center"
          style={{ color: "#94A3B8", fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif", padding: "4px 0" }}
          data-testid="enter-manually-from-error-btn">
          Enter details manually
        </button>
      </div>
    </div>
  );

  const aiDetected = !!(cardName || barcode) && step === 2 && !!capturedImage;
  const fieldStyle = (filled, hasError) => ({
    height: 48, paddingLeft: 38, paddingRight: 12,
    backgroundColor: filled && aiDetected ? "#F5F7FF" : "#FFFFFF",
    border: `1px solid ${hasError ? "#DC2626" : filled && aiDetected ? PRIMARY : "#E2E8F0"}`,
    fontSize: 15, fontFamily: "DM Sans, sans-serif", outline: "none", color: "#0F172A",
  });

  const renderStep2 = () => (
    <>
      <div className="flex-1 overflow-y-auto px-5">
        <StepIndicator step={2} />
        <h2 style={{ color: "#0F172A", fontSize: 24, fontWeight: 700, fontFamily: "Manrope, sans-serif", marginTop: 6 }}>Card details</h2>
        <p style={{ color: "#64748B", fontSize: 14, fontFamily: "DM Sans, sans-serif", marginTop: 4 }}>
          {aiDetected ? "Check the details we found — edit if anything looks wrong" : "Give your card a name and enter the number"}
        </p>
        {aiDetected && (
          <div className="flex items-center gap-2 mt-3 mb-1 px-3 py-2.5 rounded-xl" style={{ backgroundColor: "#EEF2FF", border: "1px solid #C7D2FE" }}>
            <span style={{ fontSize: 14 }}>✦</span>
            <p style={{ color: PRIMARY, fontSize: 12, fontWeight: 600, fontFamily: "DM Sans, sans-serif", margin: 0 }}>
              Details filled automatically — confirm before saving
            </p>
          </div>
        )}
        <div className="mt-5 space-y-4">
          <div>
            <label style={{ color: "#0F172A", fontSize: 13, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>Card name</label>
            <div className="relative mt-2">
              <CreditCard className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#94A3B8" }} />
              <input type="text" placeholder="e.g. Tesco Clubcard" value={cardName}
                onChange={(e) => { setCardName(e.target.value); setErrors((p) => ({ ...p, card_name: null })); }}
                className="w-full rounded-xl" style={fieldStyle(!!cardName, !!errors.card_name)} data-testid="card-name-input" />
            </div>
            {errors.card_name && <p style={{ color: "#DC2626", fontSize: 12, marginTop: 4, fontFamily: "DM Sans, sans-serif" }}>{errors.card_name}</p>}
          </div>
          <div>
            <label style={{ color: "#0F172A", fontSize: 13, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>Card number</label>
            <div className="relative mt-2">
              <BarcodeIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#94A3B8" }} />
              <input type="text" inputMode="numeric" placeholder="•••• •••• •••• ••••" value={barcode}
                onChange={(e) => { setBarcode(e.target.value); setErrors((p) => ({ ...p, barcode_number: null })); }}
                className="w-full rounded-xl" style={fieldStyle(!!barcode, !!errors.barcode_number)} data-testid="card-number-input" />
            </div>
            {errors.barcode_number && <p style={{ color: "#DC2626", fontSize: 12, marginTop: 4, fontFamily: "DM Sans, sans-serif" }}>{errors.barcode_number}</p>}
          </div>
          <div>
            <label style={{ color: "#0F172A", fontSize: 13, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
              Notes <span style={{ color: "#94A3B8", fontWeight: 500 }}>(optional)</span>
            </label>
            <textarea placeholder="e.g. member since 2019..." value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={3} className="w-full rounded-xl mt-2"
              style={{ padding: 12, backgroundColor: "#FFFFFF", border: "1px solid #E2E8F0", fontSize: 15, fontFamily: "DM Sans, sans-serif", outline: "none", resize: "none", color: "#0F172A" }}
              data-testid="card-notes-input" />
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 p-4 border-t flex-shrink-0" style={{ borderColor: "#F1F5F9" }}>
        <button onClick={() => { setCapturedImage(null); setStep(1); }}
          className="flex-shrink-0 flex items-center justify-center"
          style={{ width: 48, height: 48, borderRadius: 999, border: "1px solid #E2E8F0", backgroundColor: "#FFFFFF", color: "#0F172A" }}
          data-testid="back-to-step1-btn">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button onClick={handleSave} disabled={saving}
          className="flex-1 flex items-center justify-center gap-2 rounded-2xl"
          style={{ background: GRADIENT, color: "#FFFFFF", height: 48, fontSize: 15, fontWeight: 700, fontFamily: "DM Sans, sans-serif", boxShadow: "0 8px 20px rgba(45,91,255,0.25)", opacity: saving ? 0.65 : 1 }}
          data-testid="save-card-btn">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
          {saving ? "Saving..." : "Save card"}
        </button>
      </div>
    </>
  );

  const sheetHeight = { 1: "82vh", scanning: "72vh", error: "88vh", 2: "90vh" }[step] || "85vh";

  return (
    <div ref={overlayRef} onClick={(e) => e.target === overlayRef.current && onClose()}
      className="fixed inset-0 z-50 flex items-end animate-fadeIn"
      style={{ backgroundColor: "rgba(15,23,42,0.52)" }} data-testid="add-card-sheet-overlay">
      <div className="w-full bg-white animate-slideUp flex flex-col relative"
        style={{ height: sheetHeight, borderTopLeftRadius: 22, borderTopRightRadius: 22, overflow: "hidden", transition: "height 0.28s ease", maxWidth: 480, marginLeft: "auto", marginRight: "auto" }}
        data-testid="add-card-sheet">
        <div className="flex justify-center pt-2.5 pb-1 flex-shrink-0">
          <div style={{ width: 40, height: 4, backgroundColor: "#D1D5DB", borderRadius: 999 }} />
        </div>
        {(step === "scanning" || step === "error") && (
          <button onClick={onClose} className="absolute flex items-center justify-center"
            style={{ top: 14, right: 14, width: 34, height: 34, borderRadius: 999, backgroundColor: "#F1F5F9", color: "#64748B", zIndex: 10 }} aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        )}
        {step === 1          && renderStep1()}
        {step === "scanning" && renderScanning()}
        {step === "error"    && renderError()}
        {step === 2          && renderStep2()}
      </div>
      <style>{`
        @keyframes fadeIn  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { transform: translateY(100%) } to { transform: translateY(0) } }
        @keyframes scanLine { 0%,100% { transform: translateY(-24px); opacity: 0.4; } 50% { transform: translateY(24px); opacity: 1; } }
        .animate-fadeIn  { animation: fadeIn  0.2s ease-out; }
        .animate-slideUp { animation: slideUp 0.3s ease-out; }
      `}</style>
    </div>
  );
}

export { X };